(() => {
  const PRODUCT_CARD_SELECTOR = [
    '[data-gtm="collection-product-card"]',
    '[data-gtm="homepage-product-card"]',
    '[data-gtm="search-product-card"]',
  ].join(',');

  const pushToDataLayer = (payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  const parsePrice = (value) => {
    if (value == null || value === '') return undefined;
    const n = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : undefined;
  };

  const itemFromCard = (card, indexOverride) => {
    if (!(card instanceof Element)) return null;
    const itemId = card.getAttribute('data-gtm-item-id') || card.getAttribute('data-gtm-id') || '';
    const itemName = card.getAttribute('data-gtm-item-name') || card.getAttribute('data-gtm-name') || '';
    if (!itemId && !itemName) return null;

    const position = Number(card.getAttribute('data-gtm-position'));
    const index = Number.isFinite(indexOverride)
      ? indexOverride
      : Number.isFinite(position)
        ? position
        : undefined;

    return {
      item_id: String(itemId),
      item_name: itemName,
      item_brand: card.getAttribute('data-gtm-brand') || '',
      item_category: card.getAttribute('data-gtm-category') || '',
      item_variant: card.getAttribute('data-gtm-variant') || '',
      price: parsePrice(card.getAttribute('data-gtm-price')),
      quantity: 1,
      index,
      item_list_id: card.getAttribute('data-gtm-list-id') || card.getAttribute('data-gtm-collection') || '',
      item_list_name: card.getAttribute('data-gtm-list-name') || '',
    };
  };

  const collectItems = (selector = PRODUCT_CARD_SELECTOR) => {
    const seen = new Set();
    const items = [];
    document.querySelectorAll(selector).forEach((card) => {
      const item = itemFromCard(card, items.length);
      if (!item || seen.has(item.item_id)) return;
      seen.add(item.item_id);
      items.push(item);
    });
    return items;
  };

  const itemsSignature = (items) => items.map((item) => item.item_id).join('|');

  window.__serenettGtm = window.__serenettGtm || {};
  window.__serenettGtm.collectItems = collectItems;

  const pushEcommerceEvent = (eventName, extra, items, currency) => {
    const value =
      extra.value != null
        ? extra.value
        : items.reduce((sum, item) => sum + (parsePrice(item.price) || 0) * (item.quantity || 1), 0);
    const ecommerce = {
      currency: currency || extra.currency,
      value,
      item_list_id: extra.item_list_id,
      item_list_name: extra.item_list_name,
      items,
    };
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer({
      event: eventName,
      ...extra,
      value,
      items,
      ecommerce,
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const card = event.target instanceof Element ? event.target.closest(PRODUCT_CARD_SELECTOR) : null;
      if (!card) return;

      const item = itemFromCard(card);
      if (!item) return;

      pushEcommerceEvent(
        'serenett_select_item',
        {
          item_list_id: item.item_list_id,
          item_list_name: item.item_list_name,
          currency: card.getAttribute('data-gtm-currency') || '',
        },
        [item],
        card.getAttribute('data-gtm-currency') || '',
      );
    },
    true,
  );

  const waitForUpdatedItems = (previousSignature, selector) =>
    new Promise((resolve) => {
      const started = Date.now();
      const finish = () => resolve(collectItems(selector));
      const tick = () => {
        const items = collectItems(selector);
        if (itemsSignature(items) !== previousSignature || Date.now() - started > 4000) {
          finish();
          return;
        }
        window.setTimeout(tick, 150);
      };
      window.setTimeout(tick, 250);
    });

  const COLLECTION_SELECTOR = '[data-gtm="collection-product-card"]';
  const FILTER_USE_STORAGE_KEY = 'serenett_collection_filter_use';
  let filterUseGeneration = 0;
  let filterUseDebounceTimer = null;

  const buildFilterUseKey = (filterQuery) => filterQuery;

  const cleanLabelText = (text) => String(text || '').replace(/\s+/g, ' ').trim();

  const findFilterInput = (paramName, paramValue) => {
    const inputs = document.querySelectorAll('input[name^="filter."]');
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (input.name === paramName && input.value === paramValue) return input;
    }
    return null;
  };

  const resolveFilterValueLabel = (input, fallback) => {
    if (!(input instanceof HTMLInputElement)) return fallback;

    const dataLabel = cleanLabelText(input.getAttribute('data-label'));
    if (dataLabel) return dataLabel;

    const ariaLabel = cleanLabelText(input.getAttribute('aria-label'));
    if (ariaLabel) return ariaLabel;

    if (input.id) {
      const forLabel = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (forLabel) {
        const labeled =
          cleanLabelText(forLabel.querySelector('.checkbox__label-text')?.textContent) ||
          cleanLabelText(forLabel.textContent);
        if (labeled) return labeled;
      }
    }

    const host = input.closest(
      '.checkbox, .facets__pill-wrapper, .variant-option, .facets__inputs-list-item, label',
    );
    if (host) {
      const labeled =
        cleanLabelText(host.querySelector('.checkbox__label-text')?.textContent) ||
        cleanLabelText(host.querySelector('.facets__pill-label')?.textContent) ||
        cleanLabelText(host.querySelector('.facets__image-label')?.textContent) ||
        cleanLabelText(host.querySelector('.facets__swatch-label')?.textContent);
      if (labeled) return labeled;
    }

    return fallback;
  };

  const resolveFilterNameLabel = (input, paramName) => {
    const panel = input?.closest('details.facets__panel, .facets__panel, .facets__item');
    const panelLabel = cleanLabelText(panel?.querySelector('.facets__label')?.textContent);
    if (panelLabel) return panelLabel;

    if (/^filter\.v\.price\.(gte|lte)$/.test(paramName)) {
      const pricePanel = document.querySelector(
        'details.facets__panel:has(input[name^="filter.v.price"]), .facets__item:has(input[name^="filter.v.price"])',
      );
      const priceLabel = cleanLabelText(pricePanel?.querySelector('.facets__label')?.textContent);
      if (priceLabel) return priceLabel;
      return 'Price';
    }

    return paramName.replace(/^filter\.v\./, '').replace(/^filter\./, '');
  };

  const parseFilterEntry = (entry) => {
    const eq = entry.indexOf('=');
    if (eq === -1) return null;
    const key = entry.slice(0, eq);
    const value = entry.slice(eq + 1);
    const input = findFilterInput(key, value);
    return {
      // Primary analytics fields: frontend labels from DOM (generic for all facet types)
      filter_name: resolveFilterNameLabel(input, key),
      filter_value: resolveFilterValueLabel(input, value),
      // Debug / troubleshooting only — Shopify param key + raw URL/GID value
      filter_key: key,
      filter_raw_value: value,
    };
  };

  const pushCollectionFilterUse = (items, filterQuery, filters, collectionHandle) => {
    const key = buildFilterUseKey(filterQuery);
    try {
      if (window.sessionStorage.getItem(FILTER_USE_STORAGE_KEY) === key) return;
      window.sessionStorage.setItem(FILTER_USE_STORAGE_KEY, key);
    } catch (e) {}

    const parsedFilters = filters.map(parseFilterEntry).filter(Boolean);
    const filterNames = parsedFilters.map((f) => f.filter_name);
    const filterValues = parsedFilters.map((f) => f.filter_value);
    const filterKeys = parsedFilters.map((f) => f.filter_key);
    const filterRawValues = parsedFilters.map((f) => f.filter_raw_value);
    const currency = document.querySelector(COLLECTION_SELECTOR)?.getAttribute('data-gtm-currency') || '';
    pushEcommerceEvent(
      'serenett_collection_filter_use',
      {
        filter_query: filterQuery,
        filter_name: filterNames.join('|'),
        filter_value: filterValues.join('|'),
        filter_key: filterKeys.join('|'),
        filter_raw_value: filterRawValues.join('|'),
        filters: parsedFilters,
        filter_count: filters.length,
        collection_handle: collectionHandle,
        item_list_id: collectionHandle,
        item_list_name: (items[0] && items[0].item_list_name) || collectionHandle,
        currency,
      },
      items,
      currency,
    );
  };

  const scheduleCollectionFilterUse = (params) => {
    const filters = [];
    for (const [key, value] of params.entries()) {
      if (key.startsWith('filter.')) {
        filters.push(`${key}=${value}`);
      }
    }
    if (!filters.length) return;

    const pathParts = window.location.pathname.split('/').filter(Boolean);
    if (pathParts[0] !== 'collections') return;

    const collectionHandle = pathParts[1] || '';
    const filterQuery = filters.join('|');
    const previousSignature = itemsSignature(collectItems(COLLECTION_SELECTOR));

    if (filterUseDebounceTimer) window.clearTimeout(filterUseDebounceTimer);

    const generation = ++filterUseGeneration;

    filterUseDebounceTimer = window.setTimeout(async () => {
      if (generation !== filterUseGeneration) return;

      const items = await waitForUpdatedItems(previousSignature, COLLECTION_SELECTOR);
      if (generation !== filterUseGeneration) return;

      pushCollectionFilterUse(items, filterQuery, filters, collectionHandle);
    }, 500);
  };

  const SEARCH_SELECTOR = '[data-gtm="search-product-card"]';
  const VIEW_SEARCH_STORAGE_KEY = 'serenett_view_search_results';
  let searchResultsGeneration = 0;
  let searchResultsDebounceTimer = null;

  const getSearchTerm = () => new URLSearchParams(window.location.search).get('q') || '';

  const buildSearchResultsKey = () => {
    const params = new URLSearchParams(window.location.search);
    const filters = [];
    for (const [key, value] of params.entries()) {
      if (key.startsWith('filter.')) {
        filters.push(`${key}=${value}`);
      }
    }
    filters.sort();
    return `${getSearchTerm()}::${filters.join('|')}`;
  };

  const isSearchResultsPage = () => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    return parts[0] === 'search' && getSearchTerm().length > 0;
  };

  const pushViewSearchResults = (items, searchTerm) => {
    const key = buildSearchResultsKey();
    try {
      if (window.sessionStorage.getItem(VIEW_SEARCH_STORAGE_KEY) === key) return;
      window.sessionStorage.setItem(VIEW_SEARCH_STORAGE_KEY, key);
    } catch (e) {}

    const currency = document.querySelector(SEARCH_SELECTOR)?.getAttribute('data-gtm-currency') || '';
    pushEcommerceEvent(
      'serenett_view_search_results',
      {
        search_term: searchTerm,
        item_list_id: 'search',
        item_list_name: 'Search Results',
        currency,
      },
      items,
      currency,
    );
  };

  const scheduleViewSearchResults = (waitForChange = false) => {
    if (!isSearchResultsPage()) return;

    const searchTerm = getSearchTerm();
    const previousSignature = waitForChange ? itemsSignature(collectItems(SEARCH_SELECTOR)) : '';

    if (searchResultsDebounceTimer) window.clearTimeout(searchResultsDebounceTimer);

    const generation = ++searchResultsGeneration;

    searchResultsDebounceTimer = window.setTimeout(async () => {
      if (generation !== searchResultsGeneration) return;

      let items = collectItems(SEARCH_SELECTOR);
      if (waitForChange || !items.length) {
        items = await waitForUpdatedItems(previousSignature, SEARCH_SELECTOR);
      }
      if (generation !== searchResultsGeneration) return;
      if (!items.length) return;

      pushViewSearchResults(items, searchTerm);
    }, waitForChange ? 500 : 150);
  };

  document.addEventListener('filter:update', (event) => {
    const params = event.detail?.queryParams;
    if (params instanceof URLSearchParams) {
      scheduleCollectionFilterUse(params);
    }

    if (isSearchResultsPage()) {
      scheduleViewSearchResults(true);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleViewSearchResults(false));
  } else {
    scheduleViewSearchResults(false);
  }

  const SIGN_UP_STORAGE_KEY = 'serenett_sign_up_newsletter';
  const SIGN_UP_SUCCESS_RE = /thanks for subscribing/i;
  const SIGN_UP_FORM_SELECTOR = [
    '.email-signup__form',
    '.email-signup-block form',
    'form.klaviyo-form',
    'form[data-testid^="klaviyo-form"]',
  ].join(',');

  const extractKlaviyoFormId = (el) => {
    if (!(el instanceof Element)) return '';
    const hosts = [el, el.closest('[class*="klaviyo-form-"]'), el.closest('form')];
    for (const host of hosts) {
      if (!(host instanceof Element)) continue;
      const testId = host.getAttribute('data-testid');
      if (testId && testId.startsWith('klaviyo-form-')) {
        return testId.replace('klaviyo-form-', '');
      }
      for (const className of host.classList) {
        if (className.startsWith('klaviyo-form-')) {
          return className.replace('klaviyo-form-', '');
        }
      }
    }
    const embed = el.closest('footer')?.querySelector('[class*="klaviyo-form-"]');
    if (embed instanceof Element) {
      for (const className of embed.classList) {
        if (className.startsWith('klaviyo-form-')) {
          return className.replace('klaviyo-form-', '');
        }
      }
    }
    return '';
  };

  const resolveSignupSection = (el) => {
    if (!(el instanceof Element)) return 'newsletter';
    const block = el.closest('.email-signup-block');
    const customSection = block?.getAttribute('data-gtm-section');
    if (customSection && !/^\d+$/.test(customSection)) return customSection;
    if (el.closest('footer')) return 'footer_newsletter';
    if (el.closest('.section-faq, [data-section-type="section-faq"]')) return 'faq_newsletter';
    if (document.documentElement.getAttribute('data-gtm-page-type') === 'password') return 'password_newsletter';
    return customSection || 'newsletter';
  };

  const pushSignUp = (root) => {
    const el = root instanceof Element ? root : null;
    const formId = extractKlaviyoFormId(el);
    const section = resolveSignupSection(el);
    const dedupeKey = `${SIGN_UP_STORAGE_KEY}:${section}:${formId || 'native'}`;

    try {
      if (window.sessionStorage.getItem(dedupeKey)) return;
      window.sessionStorage.setItem(dedupeKey, '1');
    } catch (e) {}

    const block = el ? el.closest('.email-signup-block') : null;
    pushToDataLayer({
      event: 'sign_up',
      method: 'newsletter',
      page_type:
        (block && block.getAttribute('data-gtm-page-type')) ||
        document.documentElement.getAttribute('data-gtm-page-type') ||
        '',
      section,
      form_id: formId,
    });
  };

  const isSignupSuccess = (node) => {
    if (!(node instanceof Element)) return false;
    if (node.querySelector('.icon-error, .email-signup__message .icon-error')) return false;
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (SIGN_UP_SUCCESS_RE.test(text)) return true;
    return !!node.querySelector('.email-signup__message:not([id*="error"]) .email-signup__message-text');
  };

  const watchSignupForm = (form) => {
    if (!(form instanceof Element) || form.dataset.gtmSignupWatched === 'true') return;
    form.dataset.gtmSignupWatched = 'true';
    const root = form.closest('.email-signup-block') || form;
    if (isSignupSuccess(form) || isSignupSuccess(root)) {
      pushSignUp(form);
      return;
    }
    const observer = new MutationObserver(() => {
      if (!isSignupSuccess(form) && !isSignupSuccess(root)) return;
      observer.disconnect();
      pushSignUp(form);
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  };

  const scanSignupForms = (scope = document) => {
    if (!(scope instanceof Element) && scope !== document) return;
    scope.querySelectorAll(SIGN_UP_FORM_SELECTOR).forEach(watchSignupForm);
  };

  scanSignupForms();
  new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(SIGN_UP_FORM_SELECTOR)) watchSignupForm(node);
        scanSignupForms(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  let viewCartInFlight = false;
  let viewCartRetryTimer = 0;

  const cartLineToItem = (line, index) => ({
    item_id: line.sku || String(line.variant_id || line.product_id),
    item_name: line.product_title || line.title,
    item_brand: line.vendor || '',
    item_category: line.product_type || '',
    item_variant: line.variant_title || '',
    price: parsePrice((line.final_price || line.price) / 100),
    quantity: line.quantity || 1,
    index,
    item_list_id: '',
    item_list_name: '',
  });

  const pushViewCart = async (source, attempt = 0) => {
    if (viewCartInFlight) return;
    viewCartInFlight = true;
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!response.ok) return;
      const cart = await response.json();
      const items = (cart.items || []).map(cartLineToItem);
      if (!items.length) {
        if (attempt < 2) {
          window.clearTimeout(viewCartRetryTimer);
          viewCartRetryTimer = window.setTimeout(() => {
            viewCartRetryTimer = 0;
            viewCartInFlight = false;
            pushViewCart(source, attempt + 1);
          }, 300);
          return;
        }
        return;
      }

      window.clearTimeout(viewCartRetryTimer);
      viewCartRetryTimer = 0;

      const currency = cart.currency || '';
      const value = parsePrice(cart.total_price / 100);

      pushEcommerceEvent(
        'serenett_view_cart',
        {
          source,
          currency,
          value,
          item_list_id: 'cart',
          item_list_name: 'Cart',
        },
        items,
        currency,
      );
    } catch (e) {
    } finally {
      if (!viewCartRetryTimer) viewCartInFlight = false;
    }
  };

  const onCartDrawerOpened = () => {
    window.setTimeout(() => pushViewCart('cart_drawer'), 50);
  };

  const bindCartDrawerViewCart = () => {
    document.querySelectorAll('cart-drawer-component').forEach((drawer) => {
      if (!(drawer instanceof Element) || drawer.dataset.gtmViewCartBound === 'true') return;
      drawer.dataset.gtmViewCartBound = 'true';
      drawer.addEventListener('dialog:open', onCartDrawerOpened);
      const dialog = drawer.querySelector('dialog');
      if (dialog) {
        dialog.addEventListener('toggle', (event) => {
          if (event.newState === 'open') onCartDrawerOpened();
        });
      }
    });
  };

  bindCartDrawerViewCart();
  new MutationObserver(() => bindCartDrawerViewCart()).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
